/**
 * Money arithmetic shared by the API and the web
 * (docs/release-1/18-COST-AND-FINANCE-ANALYTICS.md §3.12, §5.1).
 *
 * Amounts are integers in minor units (cents). SQL never computes money: it
 * returns seconds per atom and every amount is computed here, so every total
 * is a sum of the same integers and every surface agrees.
 */

/** Currencies the Money settings offer (all 2-decimal ISO 4217). */
export const FINANCE_CURRENCIES = ['USD', 'EUR', 'GBP', 'RUB', 'UAH', 'CHF', 'CAD', 'AUD'] as const;

/** Upper bound of a price / budget (minor units) — matches the DB CHECKs. */
export const MAX_MONEY_CENTS = 1_000_000_000_000;
/** Upper bound of an hourly rate (minor units per hour). */
export const MAX_RATE_CENTS = 100_000_000;

/** round(numerator / denominator), halves away from zero. Denominator > 0. */
export function roundHalfUp(numerator: bigint, denominator: bigint): number {
  if (denominator <= 0n) throw new RangeError('roundHalfUp: denominator must be > 0');
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const q = abs / denominator;
  const r = abs % denominator;
  const rounded = r * 2n >= denominator ? q + 1n : q;
  const out = Number(negative ? -rounded : rounded);
  if (!Number.isSafeInteger(out)) throw new RangeError('roundHalfUp: result is not a safe integer');
  return out;
}

/** Value of `seconds` at `rateCents` per hour, rounded half up to a minor unit. */
export function atomValueCents(seconds: number, rateCents: number): number {
  return roundHalfUp(BigInt(Math.trunc(seconds)) * BigInt(Math.trunc(rateCents)), 3600n);
}

/**
 * Split `total` into integer parts proportional to `weights` so that the parts
 * add up to `total` exactly (largest remainder; ties go to the earlier index).
 * All-zero weights → everything to index 0 (callers handle "unattributed").
 */
export function allocateLargestRemainder(total: number, weights: readonly number[]): number[] {
  if (weights.length === 0) return [];
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) return weights.map((_, i) => (i === 0 ? total : 0));
  const exact = weights.map((w) => (total * w) / sum);
  const parts = exact.map((x) => Math.floor(x));
  let rest = total - parts.reduce((a, b) => a + b, 0);
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; rest > 0 && k < order.length; k += 1, rest -= 1) parts[order[k]!.i]! += 1;
  return parts;
}

/** "$1,234.50" in `currency` (falls back to "1234.50 XYZ" for an unknown code). */
export function formatMoney(
  minor: number,
  currency: string,
  opts: { locale?: string; compact?: boolean } = {},
): string {
  const locale = opts.locale ?? 'en-US';
  try {
    const nf = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...(opts.compact
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : Number.isInteger(minor / 100)
          ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          : {}),
    });
    return nf.format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * Parse what a person typed ("1 200,50", "1,200.50", "$60", "60") into minor
 * units. Returns null for empty / unparsable / negative input. The last `.` or
 * `,` followed by 1–2 digits is the decimal separator; every other separator
 * is grouping.
 */
export function parseMoneyInput(text: string): number | null {
  const cleaned = text.replace(/[\s  ]/g, '').replace(/[^\d.,-]/g, '');
  if (cleaned === '' || cleaned.startsWith('-')) return null;
  const m = /^(.*?)[.,](\d{1,2})$/.exec(cleaned);
  const whole = (m ? m[1]! : cleaned).replace(/[.,]/g, '');
  const frac = m ? m[2]!.padEnd(2, '0') : '00';
  if (!/^\d*$/.test(whole) || (whole === '' && !m)) return null;
  const cents = Number(whole || '0') * 100 + Number(frac);
  return Number.isSafeInteger(cents) ? cents : null;
}

/** "8h", "5h 15m", "45m" from seconds (finance copy; never negative). */
export function formatSecondsShort(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
